import React, { useEffect, useRef, useState } from 'react';
import { saveAs } from 'file-saver';
import { Alert, Box, Button, Checkbox, FileButton, Flex, Paper, Text, Title } from '@mantine/core';
import { IconDownload, IconExclamationCircle } from '@tabler/icons-react';
import JSZip from 'jszip';
import FaviconConfig from './config';

import styles from "./style.module.css";

const FaviconGeneratorComponent: React.FC = () => {
    const [checked, setChecked] = useState<Record<string, boolean>>({
        standard_favicons: false,
        web_app_manifest: false,
        additional_size_for_better_coverage: false,
        microsoft_windows: false,
        android_devices: false,
        apple_touch_icons: false
    });
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | ArrayBuffer | null>(null);
    const [resizedImages, setResizedImages] = useState<JSZip | null>(null);
    const [errorAlert, setErrorAlert] = useState<boolean>(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const timeoutRef = useRef<any>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const handleCheck = (currentTarget: any): void => {
        setChecked(prevState => ({...prevState, [currentTarget.name]: currentTarget.checked }));
    };

    const handleFileChange = (file: File | null) => {
        const reader = new FileReader();
        if (file) {
            setSelectedImage(file);
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const resizeAndCompressImage = (file: File, size: number): Promise<Blob | null> => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                const canvas = canvasRef.current;
                if (!canvas) return;
                const ctx = canvas?.getContext("2d");
                canvas.width = size;
                canvas.height = size;
                if (ctx) {
                    ctx.drawImage(img, 0, 0, size, size);
                }
                canvas.toBlob((blob) => {
                    resolve(blob);
                }, "image/jpeg", 0.8); // Adjust compression quality here (0.8 = 80% quality)
            };
            img.src = URL.createObjectURL(file);
        });
    };

    const generateFavicon = async () => {
        const faviconConfig = FaviconConfig.filter((el) => checked[el.key]);
        if (!faviconConfig.length) {
            setErrorAlert(true);
            timeoutRef.current = setTimeout(() => setErrorAlert(false), 3000);
            return;
        }
        const file = selectedImage;
        if (!file) return;

        setIsProcessing(true);
        const zip = new JSZip();

        try {
            for (const config of faviconConfig) {
                for (let i=0;i<config.sizes.length;i++) {
                    const resizedBlob = await resizeAndCompressImage(file, config.sizes[i]);
                    if (resizedBlob) {
                        zip.file(`${config.labels[i]}_${config.sizes[i]}x${config.sizes[i]}.jpg`, resizedBlob);
                    }
                }
            }
            setResizedImages(zip);
        } catch (error) {
            alert("An error occurred while processing the images. Please try again.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDownload = async () => {
        if (resizedImages) {
            const content = await resizedImages.generateAsync({ type: "blob" });
            saveAs(content, "resized_images.zip");
        }
        resetAllValues();
    }

    const resetAllValues = (): void  => { 
        setSelectedImage(null);
        setImagePreview(null);
        setResizedImages(null);
        setErrorAlert(false);
        setChecked({
            standard_favicons: false,
            web_app_manifest: false,
            additional_size_for_better_coverage: false,
            microsoft_windows: false,
            android_devices: false,
            apple_touch_icons: false
        });
    }

    return (
        <>
            <Title order={1} mb="0.5em" pl="0.5em" className={styles.title}>Generate Favicons</Title>
            <Paper radius="lg" p="2em" className={styles.paper} mb="5em" shadow="md">
                {errorAlert && 
                    <Alert 
                        variant="light" 
                        color="red" 
                        title="Error" 
                        icon={<IconExclamationCircle/>} 
                        className={styles.alert}
                    >
                        You must select at least one of the following options.
                    </Alert> 
                }
                <Flex wrap="wrap" gap="1em" justify="space-between">
                    <Box className={styles['favicon-container']}>
                        {FaviconConfig.map((el, i) => {
                            if (i < 4) {
                                return (
                                    <Box mb="1em" key={el.key}>
                                        <Checkbox
                                            checked={checked[el.key]}
                                            color="ocean-blue.6"
                                            size="md"
                                            aria-label={`Select ${el.title}`}
                                            label={el.title}
                                            name={el.key}
                                            onChange={(e) => handleCheck(e.currentTarget)}
                                        />
                                        <ul>
                                            {el?.labels?.map((label) => (
                                                <li key="label"><Text>{label}</Text></li>
                                            ))}
                                        </ul>
                                    </Box>
                                );
                            }
                        })}
                    </Box>
                    <Box>
                        {FaviconConfig.map((el, i) => {
                            if (i > 3) {
                                return (<Box mb="1em" key={el.key}>
                                    <Checkbox
                                        checked={checked[el.key]}
                                        color="ocean-blue.6"
                                        size="md"
                                        aria-label={`Select ${el.title}`}
                                        label={el.title}
                                        name={el.key}
                                        onChange={(e) => handleCheck(e.currentTarget)}
                                    />
                                    <ul>
                                        {el?.labels?.map((label) => (
                                            <li key={label}><Text>{label}</Text></li>
                                        ))}
                                    </ul>
                                </Box>);
                            }
                        })}
                        <Flex justify="left" align="center" gap="sm" >
                            <Box
                                h="128"
                                w="128" 
                                ml="2em"
                                className={styles["img-box"]}
                                style={{ backgroundImage: `url(${imagePreview})` }}
                            ></Box>
                            <Button.Group className={styles["btn-group"]}>
                                {!imagePreview ? <FileButton
                                    accept="image/png,image/jpeg,image/jpg, image/webp"
                                    onChange={handleFileChange}
                                >
{/* <<<<<<< Updated upstream */}
{/*                                     {(props) => <Button size="lg" aria-label="Upload image" {...props}>Upload image</Button>} */}
{/*                                 </FileButton> : <Button size="lg" aria-label="Generate Favicon" onClick={generateFavicon}>Generate Favicon</Button>} */}
{/*                                 <Button  */}
{/*                                     size="lg" */}
{/*                                     className={styles['download-btn']}  */}
{/*                                     onClick={handleDownload}  */}
{/*                                     aria-label="Download Image"  */}
{/*                                     disabled={resizedImages ? false : true} */}
{/*                                 > */}
{/*                                     <IconDownload style={{ width: '70%', height: '70%' }} stroke={1.5} /> */}
{/* ======= */}
                                    {(props) => <Button aria-label="Upload image" {...props}>Upload image</Button>}
                                </FileButton> : <Button aria-label="Generate Favicon" onClick={generateFavicon}>Generate Favicon</Button>}
                                <Button onClick={handleDownload} aria-label="Download Image" disabled={resizedImages ? false : true}>
                                    <IconDownload style={{ width: "70%", height: "70%" }} stroke={1.5} />
{/* >>>>>>> Stashed changes */}
                                </Button>
                            </Button.Group>
                        </Flex>
                    </Box>
                </Flex>
{/* <<<<<<< Updated upstream */}
{/*                 <canvas ref={canvasRef} style={{ display: 'none' }} /> */}
{/*             </Paper> */}
{/* ======= */}
                <canvas ref={canvasRef} style={{ display: "none" }} />
            </Paper >
{/* >>>>>>> Stashed changes */}
        </>
    );
};

export default FaviconGeneratorComponent;
